// SupportTickets.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FaSearch,
  FaEye,
  FaReply,
  FaPaperclip,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
} from "react-icons/fa";

import "./SupportTickets.css";

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTicketId, setExpandedTicketId] = useState(null); // Only ONE ticket expands at a time
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [replyText, setReplyText] = useState({});

  // ================= FETCH API =================
  const fetchTickets = async () => {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("authToken");

    try {
      setLoading(true);
      const res = await fetch(
        `https://backend.maamaas.com/food/api/tickets/user/tickets?userId=${userId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }

      const data = await res.json();
      setTickets(data || []);
    } catch (err) {
      console.error("Error fetching tickets:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // ================= FILTER =================
  const filteredTickets = useMemo(() => {
    let data = [...tickets];

    // SEARCH
    data = data.filter((ticket) =>
      ticket.message?.toLowerCase().includes(search.toLowerCase())
    );

    // FILTER
    if (filterStatus !== "ALL") {
      data = data.filter(
        (ticket) => ticket.status?.toLowerCase() === filterStatus.toLowerCase()
      );
    }

    return data.reverse();
  }, [tickets, search, filterStatus]);

  // ================= STATUS =================
  const getStatus = (status) => {
    switch (status?.toLowerCase()) {
      case "resolved":
        return {
          text: "Resolved",
          class: "resolved",
          icon: <FaCheckCircle />,
        };
      case "pending":
        return {
          text: "Pending",
          class: "pending",
          icon: <FaClock />,
        };
      default:
        return {
          text: "Open",
          class: "open",
          icon: <FaExclamationCircle />,
        };
    }
  };

  // ================= EXPAND (Only ONE expands) =================
  const toggleExpand = (id) => {
    // If the clicked ticket is already expanded, collapse it; otherwise, expand the clicked one
    setExpandedTicketId(expandedTicketId === id ? null : id);
  };

  // ================= REPLY =================
  const handleReply = (id) => {
    if (!replyText[id]) return;
    alert("Reply Sent Successfully");
    setReplyText((prev) => ({
      ...prev,
      [id]: "",
    }));
  };

  return (
    <div className="support-page">
      {/* TOP */}
      
      {/* SEARCH FILTER */}
      <div className="support-actions">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search tickets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="ALL">All Tickets</option>
          <option value="OPEN">Open</option>
          <option value="PENDING">Pending</option>
          <option value="RESOLVED">Resolved</option>
        </select>
      </div>

      {/* LOADING */}
      {loading ? (
        <div className="tickets-grid">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="ticket-loader"></div>
          ))}
        </div>
      ) : filteredTickets.length === 0 ? (
        // EMPTY
        <div className="empty-ticket">
          <img
            src="https://cdn-icons-png.flaticon.com/512/7486/7486740.png"
            alt="No tickets"
          />
          <h3>No Tickets Found</h3>
          <p>Your support tickets will appear here</p>
        </div>
      ) : (
        // TICKETS
        <div className="tickets-grid">
          {filteredTickets.map((ticket) => {
            const status = getStatus(ticket.status);
            const isExpanded = expandedTicketId === ticket.id; // Only true for the clicked ticket

            return (
              <div key={ticket.id} className={`ticket-card ${isExpanded ? "active" : ""}`}>
                {/* HEADER */}
                <div className="ticket-header">
                  <div>
                    <span className="ticket-id">Ticket #{ticket.id}</span>
                    <h3>{ticket.ticketType?.replace(/_/g, " ") || "Support Ticket"}</h3>
                  </div>
                  <div className={`status-badge ${status.class}`}>
                    {status.icon}
                    {status.text}
                  </div>
                </div>

                {/* MESSAGE */}
                <div className="ticket-message">
                  {ticket.message?.length > 120
                    ? ticket.message.slice(0, 120) + "..."
                    : ticket.message}
                </div>

                {/* DATE */}
                <div className="ticket-date">
                  {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : "-"}
                </div>

                {/* EXPAND - Only shows when this specific ticket is expanded */}
                {isExpanded && (
                  <div className="ticket-expand show">
                    <div className="expand-box">
                      <h4>Full Message</h4>
                      <p>{ticket.message || "-"}</p>
                    </div>
                    <div className="expand-box">
                      <h4>Admin Response</h4>
                      <div className="admin-response">
                        {ticket.adminResponse || "Support team will respond soon."}
                      </div>
                    </div>
                    {ticket.attachmentUrl && (
                      <div className="expand-box">
                        <h4>Attachment</h4>
                        <a
                          href={ticket.attachmentUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="attachment-btn"
                        >
                          <FaPaperclip />
                          View Attachment
                        </a>
                      </div>
                    )}
                    <div className="reply-section">
                      <textarea
                        placeholder="Write your reply..."
                        value={replyText[ticket.id] || ""}
                        onChange={(e) =>
                          setReplyText((prev) => ({
                            ...prev,
                            [ticket.id]: e.target.value,
                          }))
                        }
                      />
                      <button onClick={() => handleReply(ticket.id)}>
                        <FaReply />
                        Send Reply
                      </button>
                    </div>
                  </div>
                )}

                {/* FOOTER */}
                <div className="ticket-footer">
                  <button className="view-btn" onClick={() => toggleExpand(ticket.id)}>
                    <FaEye />
                    {isExpanded ? "Show Less" : "View Details"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SupportTickets;